/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import _ from 'underscore';

import i18n from '../../../i18n/AdHocBundle.properties';

import { IAxisItem, TAxis, TGroupedMeasures } from '../types/StateTypes';
// @ts-ignore
import QueryVariableTypes from '../../api/model/enum/QueryVariableTypes';

const groupedMeasures: TGroupedMeasures = {
    kind: 'measures',
    measures: [],
    label: i18n['adhoc.node.measures.node']
};

export default {

    getFirstItem: (axis: TAxis) :(IAxisItem | TGroupedMeasures) => axis[0],

    isMeasure: ({ kind } :any) :boolean => kind === 'measure',

    isMeasureGroup: ({ kind } :any) :boolean => kind === 'measures',

    isDate: (item :IAxisItem) :boolean => {
        const { type } = item;
        return type === QueryVariableTypes.DATE || type === QueryVariableTypes.TIMESTAMP;
    },

    isTime: (item :IAxisItem) :boolean => {
        const { type } = item;
        return type === QueryVariableTypes.TIME;
    },

    isDateTime(item :IAxisItem) :boolean {
        return this.isDate(item) || this.isTime(item)
    },

    isFloat(item :IAxisItem) {
        const { type } = item;
        return type === QueryVariableTypes.DECIMAL || type === QueryVariableTypes.BIG_DECIMAL || type === QueryVariableTypes.DOUBLE || type === QueryVariableTypes.FLOAT;
    },

    defaultCategorizer(item :IAxisItem) {
        if (this.isDateTime(item)) {
            return this.isTime(item) ? 'second' : 'day';
        }
        return undefined;
    },

    isDefaultCategorizer(item :IAxisItem) :boolean {
        const { categorizer } = item;
        return categorizer === this.defaultCategorizer(item);
    },

    allHasSummaries(axis :TAxis) :boolean {
        let hasSummaries = true;
        const flattenAxis = this.flattenAxis(axis);
        _.each(flattenAxis as IAxisItem[], (item :IAxisItem) => {
            hasSummaries = hasSummaries && Boolean(item.includeAll);
        });
        return hasSummaries;
    },

    getTimeSeriesAttributes(axis :TAxis) {
        const firstField = this.getFirstItem(axis) as IAxisItem;
        return {
            isDateTime: firstField && this.isDateTime(firstField),
            categorizer: firstField && firstField.categorizer
        };
    },

    getMeasuresCount(axis: TAxis) :number {
        let measuresCount = 0;
        _.each(axis, (item) => {
            if (this.isMeasure(item)) {
                measuresCount += 1;
            } else if (this.isMeasureGroup(item)) {
                measuresCount += (item as TGroupedMeasures).measures.length;
            }
        });
        return measuresCount;
    },

    getAllCategorizers(axis: TAxis) :string[] {
        const categorizers :string[] = [];

        _.each(axis as IAxisItem[], (item :IAxisItem) => {
            if (item.categorizer) {
                categorizers.push(item.categorizer);
            }
        });

        return categorizers;
    },

    findSimilarAxisItem(itemToFind :IAxisItem, heap :TAxis, positionToStart :number) :boolean {

        for (let j = heap.length - positionToStart - 1; itemToFind && j > -1; j -= 1) {
            const testItem = heap[j] as IAxisItem;
            if (testItem) {
                if (testItem.hierarchicalName === itemToFind.hierarchicalName
                    && testItem.aggregationFunctionName === itemToFind.aggregationFunctionName) {
                    return true;
                }
            }
        }

        return false;
    },

    transformMeasuresLevelLayout(axis: TAxis, masterAxis?: TAxis): TAxis {
        let addMeasures = true;
        const measures: TAxis = [];

        return this.multiAxisMap(axis, (item) => {
            if (this.isMeasure(item)) {
                measures.push(item);
                return groupedMeasures;
            }
            return item;
        }, masterAxis).reduceRight((memo, item) => {
            if (item === groupedMeasures) {
                if (addMeasures) {
                    memo.push({ ...groupedMeasures, measures });
                    addMeasures = false;
                }
            } else {
                memo.push(item);
            }

            return memo;
        }, [] as TAxis).reverse();
    },

    flattenAxis(axis: TAxis): TAxis {
        return _.reduce(axis, (memo :TAxis, item) => {
            if (this.isMeasureGroup(item)) {
                return memo.concat((item as TGroupedMeasures).measures);
            }
            memo.push(item);
            return memo;
        }, []);
    },

    multiAxisMap(axis: TAxis, converter: (el: any) => (any), masterAxis?: TAxis): any[] {
        // TODO: maybe we can remove this usage of master axis with time
        const result: any[] = [];
        const fullAxis = masterAxis ? [...masterAxis, ...axis] : axis;

        for (let i = axis.length - 1; i >= 0; i -= 1) {
            const item = axis[i] as IAxisItem;
            if (this.isMeasure(item) || this.isDateTime(item) || !this.findSimilarAxisItem(item, fullAxis, axis.length - i)) {
                result.push(axis[i]);
            }
        }

        return result.reverse().map(converter);
    },

    toPlacement(axis :TAxis) {

        let res;
        let fieldsCount = 0;

        const measuresCount = this.getMeasuresCount(axis);

        // multiAxisMap required in this case, because it is exact configuration,
        // which will be used for any chart and crosstab and sure will pass check for table

        const axisConverter = (el:any) => {
            if (this.isMeasure(el) || this.isMeasureGroup(el)) {
                return 'm';
            }
            fieldsCount += 1;
            return 'f';
        };

        const arr :Array<string> = this.multiAxisMap(axis, axisConverter);

        if (measuresCount > 1) {
            res = [];
            for (let i = 0; i < arr.length; i += 1) {
                if (arr[i] === 'm') {
                    for (let j = 0; j < measuresCount; j += 1) {
                        // multiAxisMap shows only location of measures, its required to
                        // put additional "m"s to cover measures size
                        res.push('m');
                    }
                } else {
                    res.push(arr[i]);
                }
            }
        } else {
            res = arr;
        }

        return {
            fieldsCount,
            measuresCount,
            placement: res.join('')
        };
    },

    aggregationType(type: string, functionName: string) {
        const dummy = { type } as IAxisItem;
        const medianType = this.isDateTime(dummy) ? type : QueryVariableTypes.DECIMAL;
        if (functionName === 'Sum') {
            return this.isFloat(dummy) ? QueryVariableTypes.BIG_DECIMAL : QueryVariableTypes.LONG;
        }
        if (functionName === 'CountAll' || functionName === 'CountDistinct') {
            return QueryVariableTypes.LONG;
        }
        if (functionName === 'Average') {
            return QueryVariableTypes.DOUBLE;
        }
        if (functionName === 'Max' || functionName === 'Median' || functionName === 'Min' || functionName === 'Mode' || functionName === 'Range' || functionName === 'StdDevP' || functionName === 'StdDevS') {
            return medianType;
        }
        if (functionName === 'RangeDays' || functionName === 'RangeHours' || functionName === 'RangeMinutes' || functionName === 'RangeMonths' || functionName === 'RangeQuarters' || functionName === 'RangeSemis' || functionName === 'RangeWeeks' || functionName === 'RangeYears') {
            return QueryVariableTypes.BIG_DECIMAL;
        }

        return medianType;
    }
}
